import React, { useState, useEffect } from 'react'
import { MapPin } from '@phosphor-icons/react'
import { Map, View } from 'ol'
import Feature from 'ol/Feature'
import Point from 'ol/geom/Point'
import TileLayer from 'ol/layer/Tile'
import VectorLayer from 'ol/layer/Vector'
import 'ol/ol.css'
import { fromLonLat } from 'ol/proj'
import { OSM } from 'ol/source'
import VectorSource from 'ol/source/Vector'
import { Icon, Style } from 'ol/style'
import Overlay from 'ol/Overlay'

import { Container, InputContainer, Row, Map as MapButton } from './styled'
import { Input } from '../../../../components/common/Input'
import { Button } from '../../../../components/common/Button'
import EmployeeMarker from '../../../../assets/images/Surveillance/mapIconEmployee.svg'

export const Status = ({
  data,
  setUnblockModal,
  unblockReason,
  setUnblockReason,
}) => {
  const answered = data.AlwaysAlertMessages.filter(
    (el) => el.answered_at,
  ).length
  const unaswered = data.AlwaysAlertMessages.filter(
    (el) => !el.answered_at,
  ).length

  const blockedUser = data.AlwaysAlertMessages[0].Employee.User.BlockedUsers

  // Get the most recent answered alert with location
  const mostRecentAnsweredAlert = data.AlwaysAlertMessages
    .filter((el) => el.answered_at && el.Location)
    .sort((a, b) => new Date(b.answered_at).getTime() - new Date(a.answered_at).getTime())[0]

  const [showMap, setShowMap] = useState(false)
  const [address, setAddress] = useState('')

  const getAddressFromCoordinates = async (lat, lon) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=18&addressdetails=1&accept-language=pt`
      )
      const data = await response.json()
             if (data.address) {
         // Use the structured address object for better formatting
         const { road, suburb, city, postcode, state, country } = data.address
         
         let formattedAddress = ''
         if (road) formattedAddress += road
         if (suburb) formattedAddress += `, ${suburb}`
         if (city) formattedAddress += `, ${city}`
         if (postcode) formattedAddress += `, ${postcode}`
         
         // Fallback to display_name if structured address is incomplete
         if (!formattedAddress && data.display_name) {
           const addressParts = data.display_name.split(', ')
           formattedAddress = addressParts.slice(0, 4).join(', ') // Take first 4 parts
         }
         
         setAddress(formattedAddress || 'Endereço não disponível')
       } else if (data.display_name) {
         // Fallback to display_name if address object is not available
         const addressParts = data.display_name.split(', ')
         const relevantParts = addressParts.slice(0, 4).join(', ') // Take first 4 parts
         setAddress(relevantParts)
       } else {
         setAddress('Endereço não disponível')
       }
    } catch (error) {
      console.error('Error fetching address:', error)
      setAddress('Erro ao buscar endereço')
    }
  }

  const handleShowMap = () => {
    if (mostRecentAnsweredAlert?.Location) {
      setShowMap(true)
      // Get address when showing the map
      getAddressFromCoordinates(
        mostRecentAnsweredAlert.Location.latitude,
        mostRecentAnsweredAlert.Location.longitude
      )
    } else {
      alert('Sem localização de resposta disponível')
    }
  }

  useEffect(() => {
    if (showMap && mostRecentAnsweredAlert?.Location) {
      const { latitude, longitude } = mostRecentAnsweredAlert.Location

             // Create map with the answer location as center
       const map = new Map({
         target: 'simpleMap',
         layers: [
           new TileLayer({
             source: new OSM(),
           }),
         ],
         view: new View({
           center: fromLonLat([longitude, latitude]),
           zoom: 15,
           padding: [20, 20, 20, 20], // Add padding to prevent marker cropping
         }),
       })

       // Create tooltip overlay
       const tooltipElement = document.createElement('div')
       tooltipElement.className = 'tooltip'
       tooltipElement.style.cssText = `
         position: absolute;
         background: rgba(0, 0, 0, 0.8);
         color: white;
         padding: 8px 12px;
         border-radius: 4px;
         font-size: 12px;
         pointer-events: none;
         z-index: 1000;
         white-space: nowrap;
         display: none;
       `
       
       const tooltip = new Overlay({
         element: tooltipElement,
         offset: [0, -10],
         positioning: 'bottom-center'
       })
       
       map.addOverlay(tooltip)

       // Add marker for the answer location
       const markerLayer = new VectorLayer({
         source: new VectorSource(),
       })

       const markerFeature = new Feature(new Point(fromLonLat([longitude, latitude])))
       markerFeature.setStyle(
         new Style({
           image: new Icon({
             anchor: [0.5, 0.5], // Center the marker on the coordinate point
             src: EmployeeMarker,
             scale: 2.5,
           }),
         }),
       )

       markerLayer.getSource().addFeature(markerFeature)
       map.addLayer(markerLayer)

       // Add pointer move event for tooltip
       let tooltipTimeout
       map.on('pointermove', (event) => {
         if (tooltipTimeout) {
           clearTimeout(tooltipTimeout)
         }
         
         const feature = map.forEachFeatureAtPixel(event.pixel, (feature) => feature)
         
                   if (feature) {
            const coordinate = feature.getGeometry().getCoordinates()
            tooltipElement.innerHTML = `
              <strong>Localização da resposta</strong><br>
              ${address ? `Endereço: ${address}<br>` : ''}
              Coordenadas: ${longitude.toFixed(6)}, ${latitude.toFixed(6)}<br>
              Data: ${new Date(mostRecentAnsweredAlert.answered_at).toLocaleString()}<br>
              Código: ${mostRecentAnsweredAlert.code}
            `
            tooltip.setPosition(coordinate)
            tooltipElement.style.display = 'block'
          } else {
           tooltipTimeout = setTimeout(() => {
             tooltipElement.style.display = 'none'
           }, 100)
         }
       })

      return () => {
        map.dispose()
      }
    }
  }, [showMap, mostRecentAnsweredAlert])

  return (
    <Container>
      <Row>
        <InputContainer width="10rem" style={{ alignSelf: 'flex-start' }}>
          <span>Status</span>
          <Button.Root
            backgroundColor={blockedUser.length > 0 ? '#F33C3C' : '#68D873'}
          >
            <Button.Content
              title={blockedUser.length > 0 ? 'Inativo' : 'Ativo'}
              color="#fff"
            />
          </Button.Root>
        </InputContainer>
      </Row>
      <Row>
        <InputContainer>
          <Input.Root>
            <Input.Label title="Alertas enviados" htmlFor="sentAlerts" />
            <Input.Wrapper backgroundColor="#f7f7f7" outlineColor="#cecece">
              <Input.Content id="" value={answered} />
            </Input.Wrapper>
          </Input.Root>
        </InputContainer>
        <InputContainer>
          <Input.Root>
            <Input.Label title="Alertas não respondidos" htmlFor="unansweredAlerts" />
            <Input.Wrapper backgroundColor="#f7f7f7" outlineColor="#cecece">
              <Input.Content value={unaswered} />
            </Input.Wrapper>
          </Input.Root>
        </InputContainer>
      </Row>
      <Row>
        <InputContainer>
          <Input.Root>
            <Input.Label title="Data do bloqueio" htmlFor="blockDate" />
            <Input.Wrapper backgroundColor="#f7f7f7" outlineColor="#cecece">
              <Input.Content
                value={
                  blockedUser.length > 0
                    ? new Date(blockedUser[0].blocked_date).toLocaleDateString()
                    : '-'
                }
                readOnly
              />
            </Input.Wrapper>
          </Input.Root>
        </InputContainer>
        <InputContainer>
          <Input.Root>
            <Input.Label title="Horário do bloqueio" htmlFor="blockTime" />
            <Input.Wrapper backgroundColor="#f7f7f7" outlineColor="#cecece">
              <Input.Content
                value={
                  blockedUser.length > 0
                    ? new Date(blockedUser[0].blocked_date).toLocaleTimeString()
                    : '-'
                }
                readOnly
              />
            </Input.Wrapper>
          </Input.Root>
        </InputContainer>
      </Row>

      {/* Location Information Section */}
      {mostRecentAnsweredAlert && (
        <>
          <Row>
            <InputContainer>
              <span>Localização da resposta</span>
            </InputContainer>
          </Row>
          <Row>
            <Input.Root>
              <Input.Label title="Data da resposta" htmlFor="responseDate" />
              <Input.Wrapper backgroundColor="#f7f7f7" outlineColor="#cecece">
                <Input.Content
                  value={
                    mostRecentAnsweredAlert.answered_at
                      ? new Date(mostRecentAnsweredAlert.answered_at).toLocaleString()
                      : '-'
                  }
                  readOnly
                />
              </Input.Wrapper>
            </Input.Root>
            <Input.Root>
              <Input.Label title="Código do alerta" htmlFor="alertCode" />
              <Input.Wrapper backgroundColor="#f7f7f7" outlineColor="#cecece">
                <Input.Content
                  value={mostRecentAnsweredAlert.code || '-'}
                  readOnly
                />
              </Input.Wrapper>
            </Input.Root>
            <MapButton
              background={showMap ? '#BB0000' : '#fff'}
              border={showMap}
              onClick={handleShowMap}
            >
              <div>
                <MapPin
                  size={20}
                  color={showMap ? '#fff' : '#BB0000'}
                  weight="fill"
                />
              </div>
              <span>Ver no mapa</span>
            </MapButton>
          </Row>

          {/* Simple Map Display */}
          {showMap && mostRecentAnsweredAlert.Location && (
            <Row>
              <InputContainer style={{ width: '100%' }}>
                <div
                  id="simpleMap"
                  style={{
                    width: '100%',
                    height: '400px',
                    border: '1px solid #cecece',
                    borderRadius: '6px'
                  }}
                />
              </InputContainer>
            </Row>
          )}
        </>
      )}

      {blockedUser.length > 0 && (
        <Row>
          <InputContainer>
            <span>Comentário pós contato</span>
            <textarea
              value={unblockReason}
              onChange={(e) => setUnblockReason(e.target.value)}
            />
          </InputContainer>
        </Row>
      )}

      {blockedUser.length > 0 && (
        <>
          <InputContainer width="fit-content" style={{ alignSelf: 'flex-end' }}>
            <Button.Root
              backgroundColor="#00517D"
              onClick={() => setUnblockModal(true)}
            >
              <Button.Content title="Reativar usuário" color="#fff" />
            </Button.Root>
          </InputContainer>
        </>
      )}
    </Container>
  )
}
